import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import FixedImage from '../../components/AdminDashboard/FixedImage';
import {
    CRow,
    CCol,
    CNav,
    CNavItem,
    CNavLink,
    CTabContent,
    CTabPane,
    CTable,
    CTableHead,
    CTableRow,
    CTableHeaderCell,
    CTableDataCell,
} from '@coreui/react-pro';
import { Link, useLocation, useParams } from 'react-router-dom';
import { selectUser } from '../../slice/userSlice';
import { DISPATCHER_PATHS, REPORTS_PATHS } from '../../constants/path';
import Access from '../../components/Access';
import { AccessType } from '../../constants/roleAccessConstant';
import type { ICarReport, CarReportApi, CarReportRemarkUI, CarReportStatusChangeUI, CarReportAssigneeHistoryUI } from './model/carReport.types';
import { mapCarReportApiToUI, mapRemarkApiToUI, mapStatusChangeApiToUI, mapAssigneeHistoryApiToUI, mapOriginalSubmissionApiToUI } from './model/carReport.mappers';
import {
    getCarReports,
    getCarReportHistory,
    createCarReportRemark,
    AttachmentPayload,
    changeCarReportStatus,
    updateCarReportAssignee,
    normalizeCarReportsResponse,
} from '../../services/carReportsAPI';
import { uploadFiles, downloadFile } from '../../services/commonAPI';
import { CXButton } from '../../components/CXForm';
import StatusChip from './components/StatusChip';
import { useAppSelector } from '../../app/hooks';
import { DownloadAttachmentIcon } from '../../assets/images';
import './style/CARReportPage.scss';
import ClosedWithoutActionConfirmModal from './modal/ClosedWithoutActionConfirmModal';
import ReOpenConfirmModal from './modal/ReOpenConfirmModal';
import RemarkModal from './modal/RemarkModal';
import StatusChangeModal from './modal/StatusChangeModal';
import EmailModal from './modal/EmailModal';
import { getCarReportTopics } from '../../services/carReportTopicAPI';
import UpdateAssigneeModal from './modal/UpdateAssigneeModal';

import {
    sectionCard,
    titleLabel,
    sectionTitle,
    label,
    value,
    ufiLinkStyle,
    rowGap,
} from './config/detailStyles';

import {
    CarReportStatus,
    STATUS_LABELS,
    STATUS_TRANSITIONS,
    normalizeStatus,
} from './config/statusConfig';

const resolvePhotoResourceKey = (ufi?: string, photoID?: string): string => {
    const rawPhotoID = String(photoID ?? '').trim();
    if (!rawPhotoID) return '';

    // New format: upload returns full key (e.g. UFI/uuid.png). Use it directly.
    if (rawPhotoID.includes('/')) return rawPhotoID;

    // Legacy format: photoID is plain id. Build UFI/image_<id>.jpg.
    const normalizedUfi = String(ufi ?? '').trim();
    return normalizedUfi ? `${normalizedUfi}/image_${rawPhotoID}.jpg` : `image_${rawPhotoID}.jpg`;
};

const RemarksSection = React.memo(function RemarksSection(props: {
    remarks: CarReportRemarkUI[];
    onAdd: () => void;
    onDownload: (fileId: string, fileName: string) => void;
}) {
    const { remarks, onAdd, onDownload } = props;

    return (
        <div style={{ ...sectionCard, marginTop: 16 }}>
            <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 12 }}>
                <div style={sectionTitle}>Remarks</div>
                <button
                    style={{
                        color: '#116F9A',
                        fontSize: 14,
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                    }}
                    onClick={onAdd}
                >
                    + Add new remark
                </button>
            </div>

            {remarks.length === 0 ? (
                <div style={value}>--</div>
            ) : (
                <div>
                    {remarks.map((r, idx) => (
                        <div key={`${r.changedAtUtc}-${idx}`} style={{ marginBottom: 16 }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8, flexWrap: 'wrap' }}>
                                <span style={{ fontWeight: 700, fontSize: 16, lineHeight: '24px', color: '#000', marginRight: 4 }}>
                                    {r.changedBy}
                                </span>
                                <span style={{ fontWeight: 700, fontSize: 16, lineHeight: '24px', color: '#000', marginRight: 8 }}>
                                    -
                                </span>
                                <span style={{ fontWeight: 400, fontSize: 16, lineHeight: '24px', color: '#000', marginRight: 12 }}>
                                    {r.content}
                                </span>
                                <span style={{ fontWeight: 500, fontSize: 14, lineHeight: '18px', color: '#808285', whiteSpace: 'nowrap' }}>
                                    {r.changedAtDisplay}
                                </span>
                            </div>

                            {r.attachment && r.attachment.length > 0 && (
                                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 8 }}>
                                    {r.attachment.map((att, attIdx) => (
                                        <button
                                            key={`${att.fileId}-${attIdx}`}
                                            onClick={() => onDownload(att.fileId, att.name)}
                                            style={{
                                                display: 'inline-flex',
                                                alignItems: 'center',
                                                padding: '6px 12px',
                                                backgroundColor: '#4C4C4C',
                                                borderRadius: 4,
                                                cursor: 'pointer',
                                                border: 'none',
                                                outline: 'none',
                                            }}
                                            aria-label={`Download ${att.name}`}
                                        >
                                            <span
                                                style={{
                                                    fontSize: 14,
                                                    color: '#FFFFFF',
                                                    maxWidth: 200,
                                                    overflow: 'hidden',
                                                    textOverflow: 'ellipsis',
                                                    whiteSpace: 'nowrap',
                                                }}
                                            >
                                                {att.name}
                                            </span>
                                            <DownloadAttachmentIcon
                                                height={14}
                                                width={14}
                                                style={{ marginLeft: 8, cursor: 'pointer', color: '#FFFFFF', fill: '#FFFFFF', pointerEvents: 'none' }}
                                            />
                                        </button>
                                    ))}
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
});

const AssigneeHistoryTable = React.memo(function AssigneeHistoryTable(props: { assigneeHistory: CarReportAssigneeHistoryUI[] }) {
    const { assigneeHistory } = props;

    return (
        <div style={{ marginBottom: 32 }}>
            <div style={{ fontWeight: 700, fontSize: 16, lineHeight: '24px', color: '#000', marginBottom: 12 }}>
                Assignee history
            </div>

            <CTable align="middle" style={{ tableLayout: 'fixed', width: '100%' }}>
                <CTableHead>
                    <CTableRow>
                        <CTableHeaderCell style={{ fontWeight: 600, fontSize: 14, lineHeight: '20px', color: '#4C4C4C', width: '33%' }}>
                            Assignee group / lead
                        </CTableHeaderCell>
                        <CTableHeaderCell style={{ fontWeight: 600, fontSize: 14, lineHeight: '20px', color: '#4C4C4C', width: '33%' }}>
                            Assigned by
                        </CTableHeaderCell>
                        <CTableHeaderCell style={{ fontWeight: 600, fontSize: 14, lineHeight: '20px', color: '#4C4C4C', width: '33%' }}>
                            Assign time
                        </CTableHeaderCell>
                    </CTableRow>
                </CTableHead>
                <tbody>
                    {assigneeHistory.length === 0 ? (
                        <tr>
                            <CTableDataCell colSpan={3}>
                                <div style={{ fontSize: 14, lineHeight: '20px', color: '#808285', padding: '16px 0' }}>--</div>
                            </CTableDataCell>
                        </tr>
                    ) : (
                        assigneeHistory.map((ah, idx) => (
                            <tr key={`${ah.assignTimeUtc}-${idx}`} style={{ height: '48px', borderBottom: '1px solid #D8D8D8' }}>
                                <CTableDataCell style={{ fontSize: 14, lineHeight: '20px', color: '#000' }}>
                                    {ah.assigneeGroup && ah.assigneeGroup.trim().length > 0
                                        ? ah.assigneeGroup
                                        : ah.assigneeLead && ah.assigneeLead.trim().length > 0
                                            ? ah.assigneeLead
                                            : '--'}
                                </CTableDataCell>
                                <CTableDataCell style={{ fontSize: 14, lineHeight: '20px', color: '#000' }}>
                                    {ah.assignedBy}
                                </CTableDataCell>
                                <CTableDataCell style={{ fontSize: 14, lineHeight: '20px', color: '#000' }}>
                                    {ah.assignTime} <span style={{ color: '#808285', fontSize: 12, marginLeft: 4 }}>UTC</span>
                                </CTableDataCell>
                            </tr>
                        ))
                    )}
                </tbody>
            </CTable>
        </div>
    );
});

const StatusChangeRecordTable = React.memo(function StatusChangeRecordTable(props: { statusChanges: CarReportStatusChangeUI[] }) {
    const { statusChanges } = props;

    return (
        <div>
            <div style={{ fontWeight: 700, fontSize: 16, lineHeight: '24px', color: '#000', marginBottom: 12 }}>
                Status change record
            </div>

            <CTable align="middle" style={{ tableLayout: 'fixed', width: '100%' }}>
                <CTableHead>
                    <CTableRow>
                        <CTableHeaderCell style={{ fontWeight: 600, fontSize: 14, lineHeight: '20px', color: '#4C4C4C', width: '33%' }}>
                            Status update
                        </CTableHeaderCell>
                        <CTableHeaderCell style={{ fontWeight: 600, fontSize: 14, lineHeight: '20px', color: '#4C4C4C', width: '33%' }}>
                            Assigned by
                        </CTableHeaderCell>
                        <CTableHeaderCell style={{ fontWeight: 600, fontSize: 14, lineHeight: '20px', color: '#4C4C4C', width: '33%' }}>
                            Change record
                        </CTableHeaderCell>
                    </CTableRow>
                </CTableHead>
                <tbody>
                    {statusChanges.length === 0 ? (
                        <tr>
                            <CTableDataCell colSpan={3}>
                                <div style={{ fontSize: 14, lineHeight: '20px', color: '#808285', padding: '16px 0' }}>--</div>
                            </CTableDataCell>
                        </tr>
                    ) : (
                        statusChanges.map((sc, idx) => (
                            <tr key={`${sc.changedAtUtc}-${idx}`} style={{ height: '48px', borderBottom: '1px solid #D8D8D8' }}>
                                <CTableDataCell>
                                    <StatusChip status={sc.statusDisplay} />
                                </CTableDataCell>
                                <CTableDataCell style={{ fontSize: 14, lineHeight: '20px', color: '#000' }}>
                                    {sc.changedBy}
                                </CTableDataCell>
                                <CTableDataCell style={{ fontSize: 14, lineHeight: '20px', color: '#000' }}>
                                    {sc.changedAtDisplay} <span style={{ color: '#808285', fontSize: 12, marginLeft: 4 }}>UTC</span>
                                </CTableDataCell>
                            </tr>
                        ))
                    )}
                </tbody>
            </CTable>
        </div>
    );
});

export default function CARReportDetailPage() {
    const { pathname: routePath } = useLocation();
    const { ufi = '' } = useParams<{ ufi: string }>();
    const location = useLocation();
    const { userId } = useAppSelector(selectUser);

    const stateReport = (location.state as any)?.report as ICarReport | undefined;

    const [report, setReport] = useState<ICarReport | undefined>(stateReport);
    const [, setLoading] = useState<boolean>(!stateReport);
    const [tabKey, setTabKey] = useState<number>(0);

    const [remarks, setRemarks] = useState<CarReportRemarkUI[]>([]);
    const [statusChanges, setStatusChanges] = useState<CarReportStatusChangeUI[]>([]);
    const [assigneeHistory, setAssigneeHistory] = useState<CarReportAssigneeHistoryUI[]>([]);

    const [isRemarkModalVisible, setIsRemarkModalVisible] = useState(false);
    const [remarkText, setRemarkText] = useState('');
    const [remarkFiles, setRemarkFiles] = useState<File[]>([]);
    const [remarkSaving, setRemarkSaving] = useState(false);
    const [remarkUploading, setRemarkUploading] = useState(false);
    const [remarkError, setRemarkError] = useState('');

    const [isStatusModalVisible, setIsStatusModalVisible] = useState(false);
    const [selectedStatus, setSelectedStatus] = useState<string>('');
    const [statusRemark, setStatusRemark] = useState('');
    const [statusFiles, setStatusFiles] = useState<File[]>([]);
    const [statusSaving, setStatusSaving] = useState(false);
    const [statusUploading, setStatusUploading] = useState(false);
    const [statusError, setStatusError] = useState('');
    const [initialFetched, setInitialFetched] = useState(false);

    const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);
    const [emailTo, setEmailTo] = useState({
        assigneeLead: false,
        assigneeGroup: false,
        caseCreator: false,
    });
    const [emailContent, setEmailContent] = useState({
        includeRemarkContent: false,
        includeRemarkAttachments: false,
        freeText: false,
        freeTextValue: '',
    });

    const [isCwaConfirmVisible, setIsCwaConfirmVisible] = useState(false);
    const [isReOpenConfirmVisible, setIsReOpenConfirmVisible] = useState(false);

    const [showUpdateModal, setShowUpdateModal] = useState(false);
    const [updateAssigneeModalKey, setUpdateAssigneeModalKey] = useState(0);

    const [availableLeadOptions, setAvailableLeadOptions] = useState<string[]>([]);
    const [availableGroupOptions, setAvailableGroupOptions] = useState<string[]>([]);

    const openUpdateAssigneeModal = useCallback(() => {
        setUpdateAssigneeModalKey((k) => k + 1);
        setShowUpdateModal(true);
    }, []);

    const closeUpdateAssigneeModal = useCallback(() => {
        setShowUpdateModal(false);
    }, []);

    const carReportId = useMemo(() => stateReport?.id || report?.id, [stateReport?.id, report?.id]);

    const reportReqIdRef = useRef(0);

    const fetchReport = useCallback(
        async (opts?: { silent?: boolean; preferId?: string }) => {
            const reqId = ++reportReqIdRef.current;

            try {
                if (!opts?.silent) setLoading(true);

                const res = await getCarReports({ ufi, comment: '' });
                const normalized = normalizeCarReportsResponse(res);
                const list = normalized.code === 0 ? (normalized.records as CarReportApi[]) : [];

                const picked =
                    (opts?.preferId ? list.find((x) => String(x.id) === String(opts.preferId)) : undefined) ||
                    list.find((x) => String(x.ufi) === String(ufi)) ||
                    list[0] ||
                    null;

                if (reqId !== reportReqIdRef.current) return;

                if (picked) setReport(mapCarReportApiToUI(picked));
            } catch (e) {
                console.error('getCarReports failed', e);
            } finally {
                if (reqId === reportReqIdRef.current && !opts?.silent) setLoading(false);
                if (reqId === reportReqIdRef.current) setInitialFetched(true);
            }
        },
        [ufi],
    );

    useEffect(() => {
        if (stateReport) {
            setReport(stateReport);
            setLoading(false);
        }
        fetchReport({ silent: !!stateReport, preferId: stateReport?.id || carReportId });
    }, [ufi, stateReport, fetchReport, carReportId]);

    const fetchHistory = useCallback(async () => {
        if (!carReportId) return;

        try {
            const res = await getCarReportHistory(carReportId);
            if (res.code !== 0 || !res.data) return;

            const { remarks: remarkApiList, statusChanges: statusChangeApiList, assigneeChanges: assigneeChangesApiList, originalSubmission } = res.data;

            setRemarks((remarkApiList || []).map(mapRemarkApiToUI));
            setStatusChanges((statusChangeApiList || []).map(mapStatusChangeApiToUI));
            setAssigneeHistory((assigneeChangesApiList || []).map(mapAssigneeHistoryApiToUI));
            mapOriginalSubmissionApiToUI(originalSubmission);
        } catch (e) {
            console.error('getCarReportHistory failed', e);
        }
    }, [carReportId]);

    const assigneeOptionsCacheRef = useRef<{ leads: string[]; groups: string[] } | null>(null);

    const fetchAssigneeOptions = useCallback(async () => {
        if (assigneeOptionsCacheRef.current) {
            setAvailableLeadOptions(assigneeOptionsCacheRef.current.leads);
            setAvailableGroupOptions(assigneeOptionsCacheRef.current.groups);
            return;
        }

        try {
            const res = await getCarReportTopics('', '', '', false);
            if (res?.code !== 0 || !Array.isArray(res?.data)) {
                setAvailableLeadOptions([]);
                setAvailableGroupOptions([]);
                return;
            }

            const topics = res.data as any[];

            const leadSet = new Set<string>();
            const groupSet = new Set<string>();

            topics.forEach((t) => {
                if (t.AssigneeType === 'ASSIGNEE_LEAD' && t.AssigneeName) leadSet.add(t.AssigneeName);
                if (t.AssigneeType === 'GROUP' && t.AssigneeName) groupSet.add(t.AssigneeName);
            });

            const leads = Array.from(leadSet).sort((a, b) => a.localeCompare(b));
            const groups = Array.from(groupSet).sort((a, b) => a.localeCompare(b));

            assigneeOptionsCacheRef.current = { leads, groups };
            setAvailableLeadOptions(leads);
            setAvailableGroupOptions(groups);
        } catch (e) {
            console.error('fetch assignee options failed', e);
            setAvailableLeadOptions([]);
            setAvailableGroupOptions([]);
        }
    }, []);

    useEffect(() => {
        if (!carReportId) return;
        fetchHistory();
    }, [carReportId, fetchHistory]);

    useEffect(() => {
        fetchAssigneeOptions();
    }, [fetchAssigneeOptions]);

    const crumbs = useMemo(
        () => [{ label: 'CAR Report Tracker', to: REPORTS_PATHS.carReportTracker }, { label: ufi }],
        [ufi],
    );

    const assignedGroupText = useMemo(() => (report?.assigneeGroup || []).join(', ') || '--', [report?.assigneeGroup]);

    const currentStatus = useMemo(() => normalizeStatus(report?.status), [report?.status]);

    const availableStatusOptions = useMemo(
        () =>
            (STATUS_TRANSITIONS[currentStatus] || []).map((s) => {
                const label = STATUS_LABELS[s];
                return { value: label, label };
            }),
        [currentStatus],
    );

    const handleDownloadAttachment = useCallback(async (fileId: string, fileName: string) => {
        await downloadFile(fileId, fileName, 'cx', 'carReport');
    }, []);

    const openRemarkModal = useCallback(() => {
        setRemarkText('');
        setRemarkFiles([]);
        setRemarkError('');
        setIsRemarkModalVisible(true);
    }, []);

    const closeRemarkModal = useCallback(() => {
        if (remarkSaving) return;
        setRemarkError('');
        setIsRemarkModalVisible(false);
    }, [remarkSaving]);

    const handleRemarkFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        const fileArray = Array.from(files);
        setRemarkFiles((prev) => [...prev, ...fileArray]);
        e.target.value = '';
    }, []);

    const handleRemarkDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (!files || files.length === 0) return;
        const fileArray = Array.from(files);
        setRemarkFiles((prev) => [...prev, ...fileArray]);
    }, []);

    const handleRemarkDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    }, []);

    const handleRemoveFile = useCallback((index: number) => {
        setRemarkFiles((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleRemoveStatusFile = useCallback((index: number) => {
        setStatusFiles((prev) => prev.filter((_, i) => i !== index));
    }, []);

    const handleUploadFilesAndRemark = useCallback(async () => {
        if (!carReportId || !report?.ufi) {
            console.error('Missing carReportId or ufi for upload');
            return;
        }
        if (!remarkText.trim()) {
            setRemarkError('Please enter remarks before uploading.');
            return;
        }

        setRemarkError('');
        setRemarkUploading(true);

        try {
            let fileIds: AttachmentPayload[] = [];

            if (remarkFiles.length > 0) {
                const path = `${report.ufi}/carsAdminAttachements/${carReportId}`;
                const response = await uploadFiles({ files: remarkFiles, path, type: 'carReport' });

                if (response?.code === 0 && Array.isArray(response?.data?.fileIds)) {
                    fileIds = response.data!.fileIds;
                } else {
                    console.error('Upload failed:', response);
                    return;
                }
            }

            setRemarkSaving(true);

            await createCarReportRemark({
                carReportId,
                content: remarkText.trim(),
                author: userId,
                attachment: fileIds.length > 0 ? fileIds : undefined,
            });

            await fetchHistory();

            setIsRemarkModalVisible(false);
            setRemarkText('');
            setRemarkFiles([]);
        } catch (error) {
            console.error('Upload or remark error:', error);
        } finally {
            setRemarkUploading(false);
            setRemarkSaving(false);
        }
    }, [carReportId, report?.ufi, remarkText, remarkFiles, userId, fetchHistory]);

    const openStatusModal = useCallback(() => {
        setSelectedStatus('');
        setStatusRemark('');
        setStatusFiles([]);
        setStatusError('');
        setIsStatusModalVisible(true);
    }, []);

    const closeStatusModal = useCallback(() => {
        if (statusSaving) return;
        setStatusError('');
        setIsStatusModalVisible(false);
    }, [statusSaving]);

    const handleStatusFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;
        if (!files || files.length === 0) return;
        const fileArray = Array.from(files);
        setStatusFiles((prev) => [...prev, ...fileArray]);
        e.target.value = '';
    }, []);

    const handleStatusDrop = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const files = e.dataTransfer.files;
        if (!files || files.length === 0) return;
        const fileArray = Array.from(files);
        setStatusFiles((prev) => [...prev, ...fileArray]);
    }, []);

    const handleStatusDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
    }, []);

    const validateStatusRemarkBeforeUpload = useCallback(() => {
        if (statusFiles.length > 0 && !statusRemark.trim()) {
            setStatusError('Please enter remarks before uploading files.');
            return false;
        }

        setStatusError('');
        return true;
    }, [statusFiles, statusRemark]);

    const handleCwaConfirmCancel = useCallback(() => {
        setIsCwaConfirmVisible(false);
        setIsStatusModalVisible(true);
        setSelectedStatus('');
    }, []);

    const handleReOpenConfirmCancel = useCallback(() => {
        setIsReOpenConfirmVisible(false);
        setIsStatusModalVisible(true);
        setSelectedStatus('');
    }, []);

    const handleUpdateStatus = useCallback(
        async (overrideStatusCode?: CarReportStatus) => {
            if (!userId) {
                setStatusError('Invalid author. Please re-login.');
                return;
            }

            if (!carReportId || !report?.ufi) {
                console.error('No carReportId or ufi, cannot update status');
                return;
            }

            let statusCode: CarReportStatus | undefined = overrideStatusCode;

            if (!statusCode) {
                if (!selectedStatus) return;

                const statusEntry = (Object.entries(STATUS_LABELS) as [CarReportStatus, string][])
                    .find(([, label]) => label === selectedStatus);

                if (!statusEntry) {
                    console.error('Unknown selected status:', selectedStatus);
                    return;
                }

                statusCode = statusEntry[0];
            }

            if (!validateStatusRemarkBeforeUpload()) return;

            setStatusError('');
            setStatusUploading(true);

            try {
                let fileIds: AttachmentPayload[] = [];

                if (statusFiles.length > 0) {
                    const path = `${report.ufi}/carsAdminAttachements/${carReportId}`;
                    const response = await uploadFiles({ files: statusFiles, path, type: 'carReport' });

                    if (response?.code === 0 && response?.data?.fileIds && Array.isArray(response.data.fileIds)) {
                        fileIds = response.data.fileIds;
                    } else {
                        console.error('Status file upload failed:', response);
                        return;
                    }
                }

                setStatusUploading(false);
                setStatusSaving(true);

                if (statusRemark.trim() || fileIds.length > 0) {
                    await createCarReportRemark({
                        carReportId,
                        content: statusRemark.trim(),
                        author: userId,
                        attachment: fileIds.length > 0 ? fileIds : undefined,
                    });
                }

                await changeCarReportStatus({ carReportId, status: statusCode, author: userId || '' });

                setReport((prev) => (prev ? { ...prev, status: STATUS_LABELS[statusCode!] } : prev));

                await fetchHistory();

                setIsStatusModalVisible(false);
                setStatusRemark('');
                setStatusFiles([]);
                setSelectedStatus('');
            } catch (e) {
                console.error('change status failed', e);
            } finally {
                setStatusUploading(false);
                setStatusSaving(false);
            }
        },
        [userId, carReportId, report?.ufi, selectedStatus, statusFiles, statusRemark, fetchHistory, validateStatusRemarkBeforeUpload],
    );

    const handleCwaConfirmContinue = useCallback(async () => {
        setIsCwaConfirmVisible(false);
        setIsStatusModalVisible(false);
        if (!userId) {
            setStatusError('Invalid author. Please re-login.');
            return;
        }
        await handleUpdateStatus('CLOSED_WITHOUT_ACTION');
    }, [userId, handleUpdateStatus]);

    const handleReOpenConfirmContinue = useCallback(async () => {
        setIsReOpenConfirmVisible(false);
        setIsStatusModalVisible(false);
        if (!userId) {
            setStatusError('Invalid author. Please re-login.');
            return;
        }
        await handleUpdateStatus('RE_OPEN');
    }, [userId, handleUpdateStatus]);

    const handleStatusUpdateClick = useCallback(() => {
        if (!validateStatusRemarkBeforeUpload()) return;

        if (currentStatus === 'NEW' && selectedStatus === STATUS_LABELS.CLOSED_WITHOUT_ACTION) {
            setIsStatusModalVisible(false);
            setIsCwaConfirmVisible(true);
            return;
        }

        if (currentStatus === 'CLOSED' && selectedStatus === STATUS_LABELS.RE_OPEN) {
            setIsStatusModalVisible(false);
            setIsReOpenConfirmVisible(true);
            return;
        }

        handleUpdateStatus();
    }, [currentStatus, selectedStatus, handleUpdateStatus, validateStatusRemarkBeforeUpload]);

    const openEmailModal = useCallback(() => {
        setEmailTo({ assigneeLead: false, assigneeGroup: false, caseCreator: false });
        setEmailContent({ includeRemarkContent: false, includeRemarkAttachments: false, freeText: false, freeTextValue: '' });
        setIsEmailModalVisible(true);
    }, []);

    const closeEmailModal = useCallback(() => {
        setIsEmailModalVisible(false);
    }, []);

    const goToFlightDetail = useCallback((ufiVal: string) => {
        const url = `${window.location.origin}${DISPATCHER_PATHS.flightDetail}?ufi=${encodeURIComponent(ufiVal)}`;
        const newWindow = window.open(url, '_blank', 'noopener,noreferrer');
        if (newWindow) newWindow.focus();
    }, []);

    if (!report) {
        if (!initialFetched) return <div style={{ padding: 16 }} />;
        return (
            <div style={{ padding: 16 }}>
                Report not found. <Link to={REPORTS_PATHS.carReportTracker}>Back to list</Link>
            </div>
        );
    }

    return (
        <>
            <div className="breadcrumbs" style={{ marginBottom: 8 }}>
                <Link
                    to={{ pathname: crumbs[0].to, state: { __fromDetail: true } }}
                    style={{ margin: 4 }}
                    onClick={() => {
                        // ensure restore flag exists for breadcrumb PUSH back
                        try {
                            sessionStorage.setItem('carReportTracker.restoreOnNextMount', '1');
                        } catch {
                            // ignore
                        }
                    }}>
                    {crumbs[0].label}
                </Link>{' '}
                /<span style={{ margin: 4 }}>{crumbs[1].label}</span>
            </div>

            <div style={{ fontWeight: 700, fontSize: 20, lineHeight: '24px', marginBottom: 20 }}>
                {report.ufi}
            </div>

            <div className="d-flex justify-content-between align-items-center" style={{ marginBottom: 10 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 60 }}>
                    <div>
                        <div style={label}>Status</div>
                        <div>
                            <StatusChip status={report.status || ''} />
                        </div>
                    </div>

                    <div>
                        <div style={label}>Assigned lead</div>
                        <div style={titleLabel}>{report.assigneeLead || '--'}</div>
                    </div>
                </div>

                <div style={{ display: 'flex', gap: 12 }}>
                    <div style={{ visibility: 'hidden' }}>
                        <Access route={routePath} access={[AccessType.UPDATE]}>
                            <CXButton outline text="Edit report" onClick={() => { }} fontSize="14px" width="120px" />
                        </Access>
                    </div>
                    <div style={{ visibility: 'hidden' }}>
                        <CXButton text="Create email" onClick={openEmailModal} fontSize="14px" width="120px" />
                    </div>
                    <Access route={routePath} access={[AccessType.UPDATE]}>
                        <CXButton outline text="Update assignee" onClick={openUpdateAssigneeModal} fontSize="14px" width="140px" />
                    </Access>
                    <Access route={routePath} access={[AccessType.UPDATE]}>
                        <CXButton outline text="Status change" onClick={openStatusModal} fontSize="14px" width="130px" />
                    </Access>

                </div>
            </div >

            <div style={{ marginBottom: 18 }}>
                <div style={label}>Assigned group</div>
                <div style={titleLabel}>{assignedGroupText}</div>
            </div>

            <CNav variant="underline" role="tablist" style={{ marginBottom: 16 }}>
                {['Report details', 'Case update history'].map((text, idx) => (
                    <CNavItem key={text}>
                        <CNavLink
                            style={{
                                fontSize: 16,
                                color: tabKey === idx ? '#116F9A' : '#4C4C4C',
                                borderBottom: tabKey === idx ? '3px solid #116F9A' : '3px solid transparent',
                            }}
                            active={tabKey === idx}
                            component="button"
                            onClick={() => setTabKey(idx)}
                        >
                            {text}
                        </CNavLink>
                    </CNavItem>
                ))}
            </CNav>

            <CTabContent>
                <CTabPane visible={tabKey === 0}>
                    {tabKey === 0 ? (
                        <>
                            <div style={sectionCard}>
                                <div style={{ ...sectionTitle, marginBottom: 18 }}>Report details</div>

                                <CRow style={{ marginBottom: rowGap }}>
                                    <CCol xs={6}>
                                        <div style={label}>Category</div>
                                        <div style={value}>{report.category || '--'}</div>
                                    </CCol>
                                    <CCol xs={6}>
                                        <div style={label}>Subject</div>
                                        <div style={value}>{report.subject || '--'}</div>
                                    </CCol>
                                </CRow>

                                <CRow style={{ marginBottom: rowGap }}>
                                    <CCol xs={12}>
                                        <div style={label}>Comment</div>
                                        <div style={value}>{report.comment || '--'}</div>
                                    </CCol>
                                </CRow>

                                <CRow style={{ marginBottom: rowGap }}>
                                    <CCol xs={6}>
                                        <div style={label}>Creator</div>
                                        <div style={value}>{report.creator || '--'}</div>
                                    </CCol>
                                    <CCol xs={6}>
                                        <div style={label}>Require response</div>
                                        <div style={value}>{report.isResponseRequiredText || '--'}</div>
                                    </CCol>
                                </CRow>

                                <CRow>
                                    <CCol xs={6}>
                                        <div style={label}>UFI</div>
                                        <a
                                            href={`${DISPATCHER_PATHS.flightDetail}?ufi=${encodeURIComponent(report.ufi || '')}`}
                                            style={ufiLinkStyle}
                                            onClick={(e) => {
                                                e.preventDefault();
                                                if (report.ufi) goToFlightDetail(report.ufi);
                                            }}
                                        >
                                            {report.ufi}
                                        </a>
                                    </CCol>

                                    <CCol xs={6}>
                                        <div style={label}>Report creation date</div>
                                        <div style={value}>
                                            {report.creationTime}{' '}
                                            <span style={{ color: '#808080', fontSize: 12 }}>UTC</span>
                                        </div>
                                    </CCol>
                                </CRow>
                            </div>

                            <div style={{ ...sectionCard, marginTop: 16 }}>
                                <div style={{ ...sectionTitle, marginBottom: 12 }}>Supporting information (optional)</div>

                                <CRow style={{ marginBottom: rowGap }}>
                                    <CCol xs={6}>
                                        <div style={label}>Airport</div>
                                        <div style={value}>{report.airport || '--'}</div>
                                    </CCol>
                                    <CCol xs={6}>
                                        <div style={label}>Chart Ref</div>
                                        <div style={value}>{report.chartRef || '--'}</div>
                                    </CCol>
                                </CRow>

                                <CRow>
                                    <CCol xs={12}>
                                        <div style={label}>Photo</div>
                                        {report.photoIDs && report.photoIDs.length > 0 ? (
                                            <div className="d-flex" style={{ gap: 12, flexWrap: 'wrap', marginTop: 8 }}>
                                                {report.photoIDs.map((key) => (
                                                    <div key={`imgUrl${key}`} className="text-center" style={{ marginRight: 8 }}>
                                                        <div
                                                            style={{
                                                                backgroundColor: '#D9D9D9',
                                                                height: '120px',
                                                                width: '120px',
                                                                position: 'relative',
                                                                borderRadius: 6,
                                                                border: '1px solid #E6E6E6',
                                                                overflow: 'hidden',
                                                            }}
                                                        >
                                                            <FixedImage
                                                                upliftType={'manual'}
                                                                user={'cx'}
                                                                type={'img'}
                                                                fileName={resolvePhotoResourceKey(report.ufi, String(key ?? ''))}
                                                            />
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : (
                                            <div style={value}>--</div>
                                        )}
                                    </CCol>
                                </CRow>
                            </div>

                            <RemarksSection remarks={remarks} onAdd={openRemarkModal} onDownload={handleDownloadAttachment} />
                        </>
                    ) : null}
                </CTabPane>

                <CTabPane visible={tabKey === 1}>
                    {tabKey === 1 ? (
                        <div style={sectionCard}>
                            <div style={{ ...sectionTitle, marginBottom: 18 }}>Case update history</div>
                            <AssigneeHistoryTable assigneeHistory={assigneeHistory} />
                            <StatusChangeRecordTable statusChanges={statusChanges} />
                        </div>
                    ) : null}
                </CTabPane>

            </CTabContent>

            <RemarkModal
                show={isRemarkModalVisible}
                onHide={closeRemarkModal}
                remarkText={remarkText}
                setRemarkText={(v) => {
                    setRemarkText(v);
                    if (remarkError) setRemarkError('');
                }}
                remarkFiles={remarkFiles}
                remarkUploading={remarkUploading}
                remarkSaving={remarkSaving}
                remarkError={remarkError}
                remarkHasError={Boolean(remarkError) && remarkFiles.length > 0 && !remarkText.trim()}
                onRemarkFileChange={handleRemarkFileChange}
                onRemarkDrop={handleRemarkDrop}
                onRemarkDragOver={handleRemarkDragOver}
                onRemoveFile={handleRemoveFile}
                onUpload={handleUploadFilesAndRemark}
            />

            <StatusChangeModal
                show={isStatusModalVisible}
                onHide={closeStatusModal}
                selectedStatus={selectedStatus}
                setSelectedStatus={setSelectedStatus}
                availableStatusOptions={availableStatusOptions}
                statusRemark={statusRemark}
                setStatusRemark={(v) => {
                    setStatusRemark(v);
                    if (statusError) setStatusError('');
                }}
                statusFiles={statusFiles}
                statusUploading={statusUploading}
                statusSaving={statusSaving}
                statusError={statusError}
                statusRemarkHasError={Boolean(statusError) && statusFiles.length > 0 && !statusRemark.trim()}
                onStatusFileChange={handleStatusFileChange}
                onStatusDrop={handleStatusDrop}
                onStatusDragOver={handleStatusDragOver}
                onRemoveStatusFile={handleRemoveStatusFile}
                onUpdateClick={handleStatusUpdateClick}
            />

            <ClosedWithoutActionConfirmModal
                show={isCwaConfirmVisible}
                onBack={handleCwaConfirmCancel}
                onConfirm={handleCwaConfirmContinue}
            />

            <ReOpenConfirmModal
                show={isReOpenConfirmVisible}
                onBack={handleReOpenConfirmCancel}
                onConfirm={handleReOpenConfirmContinue}
            />

            <EmailModal
                show={isEmailModalVisible}
                onHide={closeEmailModal}
                emailTo={emailTo}
                setEmailTo={setEmailTo}
                emailContent={emailContent}
                setEmailContent={setEmailContent}
                onSend={() => {
                    // send email logic
                }}
            />

            <UpdateAssigneeModal
                key={updateAssigneeModalKey}
                show={showUpdateModal}
                onHide={closeUpdateAssigneeModal}
                availableLeads={availableLeadOptions}
                availableGroups={availableGroupOptions}
                initialLead={''}
                initialGroups={[]}
                onConfirm={async ({ assigneeLead, assigneeGroup }) => {
                    setShowUpdateModal(false);

                    if (!carReportId || !userId) {
                        console.error('Missing carReportId or userId when updating assignee');
                        return;
                    }

                    const submittedLead = (assigneeLead || '').trim();
                    const submittedGroups = (assigneeGroup || [])
                        .map((x) => String(x).trim())
                        .filter(Boolean);

                    const willUseGroups = submittedGroups.length > 0;
                    const willUseLead = !willUseGroups && submittedLead.length > 0;

                    const nextLead = willUseGroups ? '' : (willUseLead ? submittedLead : '');
                    const nextGroups = willUseLead ? [] : (willUseGroups ? submittedGroups : []);

                    try {
                        await updateCarReportAssignee({
                            carReportId,
                            author: userId,
                            assigneeLead: nextLead,
                            assigneeGroup: nextGroups,
                        });

                        setReport((prev) => (prev ? { ...prev, assigneeLead: nextLead, assigneeGroup: nextGroups } : prev));

                        await fetchReport({ preferId: carReportId });
                        await fetchHistory();
                    } catch (e) {
                        console.error('update assignee failed', e);
                    }
                }}
            />
        </>
    );
}
